import { Decimal } from 'decimal.js';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Item, MerchantPricingBasket } from 'apis/restaurant-api/merchant-admin/purchases/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useLocaleUtils } from 'common/hooks';

import {
  ItemRoot,
  ItemInfo,
  ItemInfoRow,
  ItemCount,
  ItemName,
  ItemOptions,
  ItemOption,
  ItemOptionCount,
  ItemOptionValue,
  ItemOptionPrice,
  PurchaseWeight,
  OriginalPurchaseWeight,
  SubstitutionItem,
  SubstitutionItemName,
  StyledArrowDownAndRight,
  MultiplicationSign,
  ItemPrice,
} from './PurchaseBreakdown.styled';

type OldPurchaseBreakdownProps = {
  basket?: MerchantPricingBasket | null;
  items: Item[];
};

const OldPurchaseBreakdown = ({ items, basket }: OldPurchaseBreakdownProps) => {
  const { formatWeightQuantity } = useLocaleUtils();

  return (
    <>
      {items.map((item, index) => {
        const merchantPricingItem = basket?.items?.find(merchantItem =>
          merchantItem.breakdown.find(breakdownItem => breakdownItem.id === item.id),
        );
        const merchantPricingItemDeposit = merchantPricingItem?.deposit.full_amount;
        const merchantPricingItemDepositAmount = new Decimal(merchantPricingItemDeposit?.amount ?? '0');

        return (
          <ItemRoot key={`${item.id}-${index}`}>
            <ItemInfo>
              {item.weighted_item_info && (
                <>
                  <ItemInfoRow>
                    <PurchaseWeight>
                      {formatWeightQuantity(item.weighted_item_info.purchased_weight_in_grams, {
                        unitDisplay: 'narrow',
                      })}{' '}
                      <MultiplicationSign />
                    </PurchaseWeight>
                    <ItemName>
                      {item.name}
                      {item.weighted_item_info.weighted_item_input_type === 'number_of_items' && (
                        <>
                          {' '}
                          (
                          <FormattedMessage
                            id="purchases.display-unit-pc"
                            values={{
                              unit: item.weighted_item_info.count,
                            }}
                          />
                          )
                        </>
                      )}
                    </ItemName>
                    <ItemPrice>
                      <MonetaryAmount
                        amount={parseFloat(item.total_price.amount)}
                        currency={item.total_price.currency}
                      />
                    </ItemPrice>
                  </ItemInfoRow>

                  {item.weighted_item_info.originally_purchased_count && (
                    <ItemInfoRow>
                      <OriginalPurchaseWeight>
                        {item.weighted_item_info.originally_purchased_weight_in_grams &&
                          formatWeightQuantity(item.weighted_item_info.originally_purchased_weight_in_grams, {
                            unitDisplay: 'narrow',
                          })}
                        {item.weighted_item_info.weighted_item_input_type === 'number_of_items' && (
                          <>
                            {' '}
                            (
                            <FormattedMessage
                              id="purchases.display-unit-pc"
                              values={{
                                unit: item.weighted_item_info.originally_purchased_count,
                              }}
                            />
                            )
                          </>
                        )}
                      </OriginalPurchaseWeight>
                    </ItemInfoRow>
                  )}
                </>
              )}

              {!item.weighted_item_info && (
                <ItemInfoRow>
                  <ItemCount>
                    {item.count} <MultiplicationSign />
                  </ItemCount>
                  <ItemName>{item.name}</ItemName>
                  <ItemPrice>
                    <MonetaryAmount amount={parseFloat(item.total_price.amount)} currency={item.total_price.currency} />
                  </ItemPrice>
                </ItemInfoRow>
              )}
              {item.substitution_for && (
                <ItemInfoRow>
                  <SubstitutionItem>
                    <StyledArrowDownAndRight height="12px" /> <FormattedMessage id="purchases.substitution-for" />:{' '}
                    <SubstitutionItemName>{item.substitution_for.name}</SubstitutionItemName>
                  </SubstitutionItem>
                </ItemInfoRow>
              )}
            </ItemInfo>

            <ItemOptions>
              {item.options.map((option, idx) => (
                <ItemOption key={`${option.id}-${idx}`}>
                  <ItemOptionCount>
                    {option.count} <MultiplicationSign />
                  </ItemOptionCount>
                  <ItemOptionValue>{option.value}</ItemOptionValue>
                  {!new Decimal(option.price.amount).equals(new Decimal(0)) && (
                    <ItemOptionPrice>
                      (
                      <MonetaryAmount amount={parseFloat(option.price.amount)} currency={option.price.currency} />)
                    </ItemOptionPrice>
                  )}
                </ItemOption>
              ))}
              {!merchantPricingItemDepositAmount.isZero() && merchantPricingItemDeposit?.currency && (
                <ItemOption>
                  <ItemOptionCount>
                    1 <MultiplicationSign />{' '}
                  </ItemOptionCount>
                  <ItemOptionValue>
                    <FormattedMessage id="purchases.deposit" />
                  </ItemOptionValue>
                  <ItemOptionPrice>
                    (
                    <MonetaryAmount
                      amount={merchantPricingItemDepositAmount.toNumber()}
                      currency={merchantPricingItemDeposit.currency}
                    />
                    )
                  </ItemOptionPrice>
                </ItemOption>
              )}
            </ItemOptions>
          </ItemRoot>
        );
      })}
    </>
  );
};

export default OldPurchaseBreakdown;
